// window.fetch is needed for this to work. Polyfill added in core/main.js

const _ = require("lodash");
const Bluebird = require("bluebird");
const moment = require("moment");
const ErrorUtil = require("../../../shared/ErrorUtil");
const AT = require("./ActionTypes");
const SocketWrapper = require("./util/SocketWrapper");
const TimeFormat = require("../../../shared/TimeFormat");

// URL for socket to connect to.
const socketUrl = "//" + (global.window ? window.location.host : "fakehost");

// Debug only. Object mapping url values to an error value. If an
// API path is in this table, it will not hit the server normally and send
// a fail to the vault after a small delay. You can also use [error, moreInfo]
// as the value if you need a moreInfo field.
// You should never commit when this table is nonempty, so add a comment (like
// triple Xs) that eslint will flag to each entry.
const FORCE_FAIL_TABLE = process.env.NODE_ENV !== "production" && {};

//
// Convenience helpers for API methods
//

const createCb = function(getIgnoreGeneration, params) {
	const {
		dispatch,
		success,
		fail,
		baseActionData = {},
		transform,
		transformError,
		url,
	} = params;
	const callTimeIgnoreGeneration = getIgnoreGeneration();
	return (err, response, version) => {
		// Do version check first
		dispatch({
			type: AT.VERSION_CHECK,
			backendVersion: version,
			frontendVersion: window.BA_PACKAGE_VERSION,
		});

		if (err && transformError) {
			({err, response} = transformError(err, response));
		}
		if (err === "E_BACKEND_ERROR") {
			ErrorUtil.log(
				"E_BACKEND_ERROR",
				"Got error from backend; sending frontend diagnostics",
				{
					url,
					requestedAt: baseActionData.requestedAt,
				}
			);
		}
		if (getIgnoreGeneration() > callTimeIgnoreGeneration) {
			return;
		} else if (err === "E_NOT_LOGGED_IN") {
			// Special case: dispatch a special action outside of usual success/fail
			dispatch({type: AT.SESSION_EXPIRED, origType: fail});
		} else if (err === "E_SESSION_NOT_PLAYING") {
			// Special case: dispatch a special action outside of usual success/fail
			dispatch({type: AT.SESSION_NOT_PLAYING, origType: fail});
		} else if (err) {
			if (process.env.NODE_ENV !== "production") {
				// eslint-disable-next-line no-console
				console.log(
					"API error!",
					"| Endpoint:",
					url,
					"| Message:",
					err,
					"| Full response:",
					response
				);
			}
			if (fail) {
				const errAction = _.extend({}, baseActionData, {
					type: fail,
					error: err,
				});
				if (err === "E_NO_IMITATING") {
					errAction.url = url;
				}
				if (response && response.moreInfo) {
					errAction.moreInfo = response.moreInfo;
				}
				dispatch(errAction);
			}
		} else if (success) {
			dispatch(
				_.extend({}, baseActionData, {type: success}, transform(response))
			);
		}
	};
};

const dispatchForcedFail = function(cb, url, errorMessage) {
	// eslint-disable-next-line no-console
	console.log("Forcing " + url + " to fail due to FORCE_FAIL_TABLE in API.js.");
	let moreInfo;
	if (Array.isArray(errorMessage)) {
		[errorMessage, moreInfo] = errorMessage;
	}
	setTimeout(() => {
		cb(errorMessage || "DEBUG: forced API call to fail", {moreInfo});
	}, 200);
};

const doStandardFetchCall = function(dispatch, params, miscData) {
	const {type, url, delay, timeout, baseActionData, method = "POST"} = params;
	const {lockErr, getIgnoreGeneration, now} = miscData;
	const payload = _.extend(
		{
			userTime: TimeFormat.getDateTimeString(moment()),
		},
		params.payload
	);

	params.dispatch = dispatch;
	// Optional overrides (e.g. resendCheckoutEmails) so we always dispatch
	// success/fail even if getSuccessType/getFailType would return null.
	params.success =
		params.successActionOverride ?? (type ? AT.getSuccessType(type) : null);
	params.fail =
		params.failActionOverride ?? (type ? AT.getFailType(type) : null);

	params.baseActionData = baseActionData || {};
	params.baseActionData.requestedAt = now;
	params.baseActionData.payload = payload;

	if (!params.transform) {
		params.transform = response => ({data: response});
	}

	// _.once used to ensure timeout and main response don't both run.
	const cb = _.once(createCb(getIgnoreGeneration, params));

	const makeFetchCallPreDelay = () => {
		// TODO: params.background and params.whenDisconnected === "queueCall" now
		// unused; maybe both should be removed?

		// Timeout-related state
		const shouldUseTimeout =
			!params.noNeedForReturn && _.isFinite(timeout) && timeout > 0;
		let timeoutHandle;
		const cancelTimeout = () => {
			timeoutHandle && clearTimeout(timeoutHandle);
		};

		const headers = {
			"Content-Type": "application/json",
			"x-frontend-version": window.BA_PACKAGE_VERSION,
		};

		if (params.frontendUser?.userID) {
			headers["x-frontend-userid"] = params.frontendUser?.userID;
		}

		let version;
		let retriesLeft = 3;
		let abortCtrl;
		const makeFetchCall = () => {
			if (global.AbortController) {
				abortCtrl = new AbortController();
			}
			window
				.fetch(url, {
					method,
					credentials: "same-origin",
					headers,
					body: method === "GET" ? null : JSON.stringify(payload),
					signal: abortCtrl && abortCtrl.signal,
				})
				.then(resp => {
					version = resp.headers.get("x-backend-version");
					if (!resp.ok) return Bluebird.reject({status: resp.status, version});
					return resp.json();
				})
				.then(resp => {
					cancelTimeout();
					if (resp.error) {
						return cb(resp.error || "E_BACKEND_ERROR", resp, version);
					} else {
						return cb(null, resp, version);
					}
				})
				.catch(ex => {
					cancelTimeout();
					// Unauthenticated
					if (ex && ex.status === 403) {
						cb("E_NOT_LOGGED_IN", ex, version);
					} else if (ex && ex.message === "Load failed" && retriesLeft > 0) {
						retriesLeft--;
						setTimeout(() => {
							makeFetchCall();
						}, 200);
						return;
					} else {
						cb("E_NOT_CONNECTED", ex, version);
					}
				});
		};

		// delay exists for debugging only. Make sure production doesn't get it on accident.
		if (process.env.NODE_ENV !== "production" && delay) {
			// eslint-disable-next-line no-console
			console.log("Starting a delay for API request", url);
			setTimeout(() => {
				makeFetchCall();
			}, delay);
		} else {
			makeFetchCall();
		}
		if (shouldUseTimeout) {
			timeoutHandle = setTimeout(() => {
				abortCtrl && abortCtrl.abort();
				cb("E_REQUEST_TIMEOUT");
				// _.once prevents
			}, timeout);
		}
	};

	if (lockErr) {
		cb(lockErr);
	} else if (process.env.NODE_ENV !== "production" && FORCE_FAIL_TABLE[url]) {
		dispatchForcedFail(cb, url, FORCE_FAIL_TABLE[url]);
	} else {
		makeFetchCallPreDelay();
	}
};

//
// Main API calling functions, all exported via makeAPIForVault
//

// The objects returned from methods on the mainMethods object can have the
// following properties:

// url: REQUIRED. The name of the endpoint, e.g. "/api/user/current".
// Usually a fixed value in each function.

// payload: REQUIRED. The payload to pass to that endpoint as a
// javascript object. Some fields, like userTime, may be automatically added
// by the infrastructure here.

// type: REQUIRED. The vault action type of the request action, from which
// the success and fail action types can be computed.

// baseActionData: Optional javascript object with data to include on the API
// response action. It is not included in the request payload.

// TODO: Do implementation for this.
// whenDisconnected: How this endpoint should behave if we know we have no
// internet at the time the request is made. If you don't check connectedness
// yourself in vault code, this is required, and ErrorUtil will be called if we
// reach an endpoint without this set while disconnected. The value should be a
// string from this list:
// - queueCall: Request normally, which will wait for connectedness to come
//   back or timeout if it takes too long.
// - failFast: Immediately dispatch a fail action with error E_NOT_CONNECTED.
// - doNothing: Don't make a request or dispatch any response action.

// (Properties below here are not as commonly used.)

// allowStateReset: If true, dispatch the response action even if a state
// reset happened after the request. This is the opt-out referred to in
// MaxResetSuppression.js.

// transform: Optional function, which is unused currently. On success, this
// receives the response and returns an object to extend the response action
// with. It defaults to placing the full response in action.data.

// transformError: Optional function. If the request errors, this receives
// (err, response) and returns {err, response} to replace them with. response
// is the full socket response, and err is the string error extracted from it
// by the SocketWrapper. Note that by returning {err: null, response}, the
// response will be handled as a success.

// delay: For development only. Delay making the request to the backend by
// the specified number of milliseconds. Can help test weird timing issues.

// delayResponse: For development only. Delay handling a response from the
// backend by the specified number of milliseconds. Can help test weird timing
// issues.

const mainMethods = {
	// slot objects are made by functions in the Slots.js table.
	loadSlot(slot) {
		return {
			url: slot.url,
			payload: slot.payload,
			type: slot.action,
			baseActionData: {slot},
			whenDisconnected: "failFast",
		};
	},

	// mslot objects are made by functions in the MultiSlots.js table.
	loadMultiSlot(mslot, ids) {
		const payload = _.extend({}, mslot.payload || {}, {
			[mslot.payloadIDsKey]: ids,
		});
		return {
			url: mslot.url,
			payload,
			type: mslot.action,
			baseActionData: {mslot, ids},
			whenDisconnected: "failFast",
		};
	},

	passwordReset(username, teacherData, purchaseID, possibleDomain) {
		return {
			url: "/api/user/passwordReset",
			payload: {
				username,
				possibleDomain,
				isFromTeacher: !!teacherData,
				classID: teacherData && teacherData.classID,
				purchaseID,
			},
			type: AT.PASSWORD_RESET_REQUEST,
			whenDisconnected: "doNothing",
		};
	},
	setupAccountPassword(
		email,
		password,
		code,
		codeType,
		redirectURL,
		passwordRequired
	) {
		return {
			url: "/api/user/setupAccountPassword",
			payload: {password, email, code, codeType, redirectURL, passwordRequired},
			baseActionData: {codeType, redirectURL},
			type: AT.ACCOUNT_PASSWORD_SETUP_REQUEST,
		};
	},
	generateParentRandomUsernames(userID, registrationCode) {
		return {
			url: "/api/user/generateRandomUsernames",
			payload: {userID, registrationCode},
			type: AT.GET_RANDOM_USERNAMES_FOR_PARENT_REQUEST,
		};
	},
	parentUpdateUnregisteredUser(userID, registrationCode, user, event) {
		return {
			url: "/api/user/updateRegistration",
			payload: {registrationCode, user: _.extend({userID}, user), event},
			type: AT.PARENT_UPDATE_UNREGISTERED_USER_REQUEST,
		};
	},
	parentUpdatePrimarySetupUser(userID, registrationCode, user, step, nextStep) {
		return {
			url: "/api/user/updateRegistration",
			payload: {
				registrationCode,
				user: _.extend({userID}, user),
				step,
				...(nextStep ? {nextStep} : {}),
			},
			type: AT.STUDENT_PRIMARY_SETUP_ADVANCE_STEP_REQUEST,
		};
	},

	parentSecondaryRegistrationUpdateStudent(
		studentID,
		platformStudentID,
		data,
		step
	) {
		// Slightly clunky payload; needed since these values are used
		// in the PARENT_UPDATE_UNREGISTERED_USER_SUCCESS handler
		const payload = {
			...data,
			...data.settings,
			studentID,
			platformStudentID,
			step,
		};
		payload.user = {...payload, userID: studentID};
		return {
			url: "/api/user/updateStudent",
			payload,
			type: AT.PARENT_UPDATE_UNREGISTERED_USER_REQUEST,
		};
	},
	generateUsernameNumberSegment(studentID, registrationCode, usernameParts) {
		return {
			url: "/api/user/generateUsernameNumberSegment",
			payload: {studentID, registrationCode, usernameParts},
			type: AT.STUDENT_PRIMARY_SETUP_GENERATE_USERNAME_REQUEST,
		};
	},
	getUnavailableUsernameCombinations() {
		return {
			url: "/api/user/getUnavailableUsernameCombinations",
			method: "GET",
			type: AT.GET_UNAVAILABLE_USERNAME_COMBINATIONS_REQUEST,
		};
	},
	updateUser(user, baseUserData) {
		// User needs to have userID
		return {
			url: "/api/user/update",
			payload: {user},
			baseActionData: {baseUserData},
			type: AT.UPDATE_USER_REQUEST,
		};
	},
	acceptPrivacy() {
		return {
			url: "/api/user/acceptPrivacy",
			payload: {},
			type: AT.ACCEPT_PRIVACY_REQUEST,
			whenDisconnected: "queueCall",
		};
	},
	savePageData(
		mostRecentPageData,
		viewedPages,
		sequenceData,
		lastLevelNotified,
		eventsTriggered
	) {
		return {
			url: "/api/user/updateActivityData",
			payload: {
				updateData: {
					mostRecentPageData,
					viewedPages,
					sequenceData,
					lastLevelNotified,
					eventsTriggered,
				},
			},
			type: AT.UPDATE_PAGE_DATA_REQUEST,
			background: true,
			whenDisconnected: "failFast",
		};
	},
	saveActivityTimes(activityList) {
		return {
			url: "/api/user/updateActivityTimes",
			payload: {
				activityList,
			},
			type: AT.UPDATE_ACTIVITY_TIMES_REQUEST,
			background: true,
			whenDisconnected: "failFast",
		};
	},
	sendBugReport(payload) {
		return {
			url: "/debug/reportBug",
			payload,
			type: AT.SEND_BUG_REPORT_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	loadUser() {
		return {
			url: "/api/user/current",
			payload: {},
			type: AT.LOAD_USER_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	loadPlatformUserBasics(platformUserID, userID) {
		return {
			url: "/api/user/getPlatformAccount",
			payload: {platformUserID, userID},
			type: AT.LOAD_USER_PLATFORM_BASICS_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	loadPlatformUserPlacement(platformUserID, userID) {
		return {
			url: "/api/user/getPlacementTest",
			payload: {platformUserID, userID},
			type: AT.LOAD_USER_PLATFORM_PLACEMENT_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	updateStudentGradeFromPlacementTest(userID, payload) {
		return {
			url: "/api/user/updateStudentGradeFromPlacementTest",
			payload: _.extend({userID}, payload),
			type: AT.STUDENT_ONBOARDING_PLACEMENT_COMPLETE_REQUEST,
		};
	},
	loadChapter(chapterID, userID) {
		const url = "/api/trainer/chapter/byID";
		if (!chapterID || !userID) {
			ErrorUtil.log(
				"E_TRAINER_LOAD_CHAPTER_MISSING_ID",
				`Attempted to call loadChapter on the frontend without a chapterID or userID`,
				{
					chapterID,
					userID,
					url,
				}
			);
		}

		return {
			url,
			payload: {chapterID, userID},
			type: AT.LOAD_CHAPTER_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	loadLibraryChapters(chapterIDs, userID) {
		return {
			url: "/api/library/chapter/byID",
			payload: {chapterIDs, userID},
			type: AT.LOAD_LIBRARY_CHAPTERS_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	loadLibraryPages(chapterID, bookHash, pageNumbers) {
		return {
			url: "/api/library/fetchPage",
			payload: {bookHash, pageNumbers},
			type: AT.LOAD_LIBRARY_PAGES_REQUEST,
			baseActionData: {chapterID},
			whenDisconnected: "failFast",
		};
	},
	loadDemoLibraryPages(chapterID, bookHash, pageNumbers) {
		return {
			url: "/api/library/fetchDemoPage",
			payload: {bookHash, pageNumbers},
			type: AT.LOAD_LIBRARY_PAGES_REQUEST,
			baseActionData: {chapterID},
			whenDisconnected: "failFast",
		};
	},
	loadHistory(blockID) {
		return {
			url: "/api/trainer/result/completedByBlockID",
			payload: {blockID},
			type: AT.BLOCK_HISTORY_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	loadLabType(typeID, userID) {
		return {
			url: "/api/lab/byType",
			payload: {typeID, userID},
			type: AT.LOAD_LAB_TYPE_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	loadAllLabTypes(userID) {
		return {
			url: "/api/lab/allLabTypes",
			payload: {userID},
			type: AT.LOAD_ALL_LAB_TYPES_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	playBlock(
		blockID,
		isTrophyRequest,
		discardLimitedResults,
		forceResume,
		position,
		forcePlayProblemIndex
	) {
		return {
			url: "/api/trainer/result/byBlockID",
			payload: {
				blockID,
				isTrophyRequest,
				discardLimitedResults,
				forceResume,
				isPositionRequest: !!position,
				position,
				forcePlayProblemIndex,
			},
			type: AT.PLAY_BLOCK_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	playStudentTutorialBlock(restart = false) {
		return {
			url: "/api/trainer/result/byBlockID",
			payload: {
				miscBlockName: "tutorial",
				forceResume: !restart,
				forceNewResult: restart,
			},
			type: AT.PLAY_STUDENT_TUTORIAL_BLOCK_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	getTutorialVideo(title) {
		return {
			url: "/api/tutorial/getTutorialVideo",
			payload: {
				title,
			},
			type: AT.STUDENT_TUTORIAL_GET_VIDEO_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	getTutorialBookPage() {
		return {
			url: "/api/tutorial/getTutorialBookPage",
			payload: {},
			type: AT.STUDENT_TUTORIAL_GET_BOOK_PAGE_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	updateTutorialProgress(part, step, totalTimesModalOpened) {
		return {
			url: "/api/tutorial/updateTutorialProgress",
			payload: {
				part,
				step,
				totalTimesModalOpened,
			},
			type: AT.STUDENT_TUTORIAL_UPDATE_PROGRESS_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	playBookTestBlock(miscBlockName, getNewResultIfLastResultFinished = false) {
		return {
			url: "/api/trainer/result/byBlockID",
			payload: {
				miscBlockName,
				forceResume: !getNewResultIfLastResultFinished,
			},
			type: AT.PLAY_BOOK_TEST_BLOCK_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	playLabBlock(blockID, isReplayRequest) {
		return {
			url: "/api/lab/result/byBlockID",
			payload: {
				blockID,
				forceNewResult: isReplayRequest,
			},
			type: AT.PLAY_LAB_BLOCK_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	requestSpecificSet(
		blockID,
		position,
		originalSetID,
		forceResume,
		forceNewResult,
		resultType
	) {
		let url, actionType;
		if (resultType === "lab") {
			url = "/api/lab/result/byBlockID";
			actionType = AT.PLAY_LAB_BLOCK_REQUEST;
		} else {
			url = "/api/trainer/result/byBlockID";
			actionType = AT.PLAY_BLOCK_REQUEST;
		}
		return {
			url,
			payload: {
				blockID,
				isPositionRequest: true,
				position,
				originalSetID,
				forceResume,
				forceNewResult,
			},
			baseActionData: {forceNewResult},
			type: actionType,
			whenDisconnected: "failFast",
		};
	},
	updateResult(
		result,
		resultSource = {},
		lastFail = false,
		ignoreSubmitToken = false
	) {
		return {
			url: "/api/trainer/result/update",
			payload: {
				result,
				blockID: resultSource.blockID,
				lastFail,
				resultSource,
				ignoreSubmitToken,
			},
			type: AT.UPDATE_RESULT_REQUEST,
			background: true,
			whenDisconnected: "failFast",
		};
	},
	updateLibraryChapterData(chapterID, userID, chapterData, roomData) {
		return {
			url: "/api/library/chapter/update",
			payload: {chapterID, userID, chapterData, roomData},
			type: AT.UPDATE_LIBRARY_CHAPTER_DATA_REQUEST,
			background: true,
			whenDisconnected: "failFast",
		};
	},
	loadTheaterChapters(chapterIDs, userID) {
		return {
			url: "/api/theater/byChapterID",
			payload: {chapterIDs, userID},
			type: AT.LOAD_THEATER_CHAPTER_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	loadTheaterSet(videoSetID, userID) {
		return {
			url: "/api/theater/bySetID",
			payload: {videoSetID, userID},
			type: AT.LOAD_THEATER_SET_REQUEST,
			whenDisconnected: "failFast",
		};
	},
	updateTheaterData(userID, videoSetID, theaterData) {
		return {
			url: "/api/theater/update",
			payload: {userID, videoSetID, theaterData},
			type: AT.UPDATE_THEATER_DATA_REQUEST,
			background: true,
			whenDisconnected: "failFast",
		};
	},

	quitBlock(blockID) {
		return {
			url: "/api/trainer/result/quitByBlockID",
			payload: {blockID, quitResult: true},
			type: AT.QUIT_TEST_BLOCK_REQUEST,
		};
	},

	// scopeInfo: {userID, classID}, set one
	loadLocks(scopeInfo) {
		if (scopeInfo.classID) {
			return {
				url: "/api/school/getClassLocks",
				payload: {classID: scopeInfo.classID},
				type: AT.LOAD_LOCKS_REQUEST,
				whenDisconnected: "failFast",
			};
		} else {
			return {
				url: "/api/user/getUserLocks",
				payload: {userID: scopeInfo.userID},
				type: AT.LOAD_LOCKS_REQUEST,
				whenDisconnected: "failFast",
			};
		}
	},

	// scopeInfo: {userID, classID}, set one
	// itemInfo: {gradeID, chapterID, blockID}, set one
	// lockStatus: null|locked|unlocked
	setLockOverride(scopeInfo, itemInfo, lockStatus, options) {
		const {loadAllLocks = false} = options || {};
		if (scopeInfo.classID) {
			return {
				url: "/api/school/setClassLockOverride",
				payload: {
					classID: scopeInfo.classID,
					itemInfo,
					lockStatus,
					loadAllLocks,
				},
				type: AT.SET_LOCK_OVERRIDE_REQUEST,
				baseActionData: {},
				whenDisconnected: "failFast",
			};
		} else {
			return {
				url: "/api/user/setUserLockOverride",
				payload: {
					userID: scopeInfo.userID,
					itemInfo,
					lockStatus,
					loadAllLocks,
				},
				type: AT.SET_LOCK_OVERRIDE_REQUEST,
				baseActionData: {},
				whenDisconnected: "failFast",
			};
		}
	},

	setSpaceLockOverride(space, lockStatus, userID, classID) {
		let type;
		if (space === "beastbuilder") {
			if (userID) type = AT.SET_STUDENT_BEASTBUILDER_LOCK_OVERRIDE_REQUEST;
			else if (classID) type = AT.SET_CLASS_BEASTBUILDER_LOCK_OVERRIDE_REQUEST;
		} else if (space === "leaderboards") {
			// TODO: Eventually teachers will be able to lock leaderboards and
			// this will need to be updated.
			type = AT.SET_STUDENT_LEADERBOARDS_LOCK_OVERRIDE_REQUEST;
		}
		if (userID) {
			return {
				url: "/api/user/setUserLockOverride",
				payload: {
					userID,
					itemInfo: {spaceID: space},
					lockStatus,
				},
				type,
				baseActionData: {userID},
				whenDisconnected: "failFast",
			};
		} else if (classID) {
			return {
				url: "/api/school/setClassLockOverride",
				payload: {
					classID,
					itemInfo: {spaceID: space},
					lockStatus,
				},
				type,
				baseActionData: {classID},
				whenDisconnected: "failFast",
			};
		}
	},

	unlockBookTest(classID, blockID, bookTest) {
		return {
			url: "/api/school/setClassLockOverride",
			payload: {
				classID,
				itemInfo: {blockID},
				lockStatus: "unlocked",
			},
			type: AT.REPORT_UNLOCK_BOOK_TEST_REQUEST,
			baseActionData: {classID, bookTest},
			whenDisconnected: "failFast",
		};
	},

	submitContactForm(form, diagnostics) {
		return {
			url: "/api/store/contact",
			payload: _.extend({}, form, {diagnostics}),
			type: AT.SUBMIT_CONTACT_FORM_REQUEST,
		};
	},

	emailSignUp(email, role) {
		return {
			url: "/api/mailingList/subscribe",
			payload: {email, role},
			type: AT.EMAIL_SIGN_UP_REQUEST,
		};
	},

	emailSignUpForDemo(email, role) {
		return {
			url: "/api/mailingList/subscribe",
			payload: {email, role, demo: true},
			type: AT.DEMO_EMAIL_SIGN_UP_REQUEST,
		};
	},

	emailSignUpAdditionalRecipients(addEmails, removeEmails, studentID, userID) {
		return {
			url: "/api/mailingList/subscribeAdditional",
			payload: {addEmails, removeEmails, studentID, userID},
			type: AT.SIGN_UP_ADDITIONAL_EMAIL_RECIPIENTS_REQUEST,
		};
	},

	resendEmailConfirmation(email, emailType, oldEmail) {
		return {
			url: "/api/user/sendEmailConfirm",
			payload: {email, emailType, oldEmail},
			type: AT.EMAIL_CONFIRMATION_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	saveCart(cart) {
		return {
			url: "/api/store/saveCart",
			payload: {cart},
			type: AT.SAVE_CART_REQUEST,
			background: true,
			whenDisconnected: "failFast",
		};
	},

	startPaypal(cart) {
		return {
			url: "/api/store/startPayPal",
			payload: {cart},
			type: AT.START_PAYPAL_REQUEST,
		};
	},

	verifyNewAccount(email, password) {
		return {
			url: "/api/user/checkExistence",
			payload: {email, password},
			type: AT.VERIFY_NEW_ACCOUNT_REQUEST,
		};
	},

	checkUserExists(data) {
		return {
			url: "/api/user/checkExistencePasswordOptional",
			payload: data,
			type: AT.CHECK_USER_EXISTS_REQUEST,
		};
	},

	verifyRedeemCodes(codes) {
		return {
			url: "/api/subscription/checkRegistrationCodes",
			payload: {codes},
			type: AT.VERIFY_REDEEM_CODES_REQUEST,
		};
	},

	redeemSubscriptionCodes(parent, students, codes) {
		return {
			url: "/api/subscription/redeemCode",
			payload: {parent, students, codes},
			type: AT.REDEEM_SUBSCRIPTION_CODES_REQUEST,
		};
	},

	getCoupon(couponCode) {
		return {
			url: "/api/store/coupons/get",
			payload: {code: couponCode},
			baseActionData: {isPrepareOrder: false},
			type: AT.GET_COUPON_REQUEST,
		};
	},

	getCouponWithTaxes(data = {}) {
		// data should contain cart, shippingAddress, couponCode, verifyAddress.

		// This is separate from the prepareOrder method so that it can use the
		// GET_COUPON response actions instead.
		return {
			url: "/api/store/prepareOrder",
			payload: data,
			baseActionData: {isPrepareOrder: true},
			type: AT.GET_COUPON_REQUEST,
		};
	},

	getCouponForSubChange(couponCode) {
		return {
			url: "/api/store/coupons/get",
			payload: {code: couponCode},
			type: AT.SUBSCRIPTION_CHANGE_GET_COUPON_REQUEST,
		};
	},

	prepareOrder(data = {}) {
		// data should contain cart, shippingAddress, couponCode, verifyAddress.
		return {
			url: "/api/store/prepareOrder",
			payload: data,
			type: AT.PREPARE_ORDER_REQUEST,
		};
	},

	placeOrder(data = {}) {
		// data should contain cart, account, shipping, billing, contact,
		// creditCard, prices, couponCode.
		return {
			url: "/api/store/checkout",
			payload: data,
			type: AT.PLACE_ORDER_REQUEST,
		};
	},

	resendCheckoutEmails(orderID, confirmEmail) {
		return {
			url: "/api/store/resendCheckoutEmails",
			payload: {orderID, confirmEmail},
			type: AT.RESEND_CHECKOUT_EMAILS_REQUEST,
			successActionOverride:
				AT.RESEND_CHECKOUT_EMAILS_SUCCESS || "_@SUCCESS_RESEND_CHECKOUT_EMAILS",
			failActionOverride:
				AT.RESEND_CHECKOUT_EMAILS_FAIL || "_@FAIL_RESEND_CHECKOUT_EMAILS",
			whenDisconnected: "failFast",
		};
	},

	loadHearAboutBaSurveyStatus(email) {
		return {
			url: "/api/store/hearAboutBaSurvey/status",
			payload: {email},
			type: AT.LOAD_HEAR_ABOUT_BA_SURVEY_STATUS_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	submitHearAboutBaSurvey(payload) {
		return {
			url: "/api/store/hearAboutBaSurvey",
			payload,
			type: AT.SUBMIT_HEAR_ABOUT_BA_SURVEY_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	findLocalAcademy(zipcode) {
		return {
			url: "/api/store/findLocalAcademy",
			payload: {zipcode},
			type: AT.LOCAL_ACADEMY_REQUEST,
		};
	},

	// ClassWallet endpoints

	createClassWalletOrder(data = {}) {
		return {
			url: "/api/classwallet/create",
			payload: data,
			type: AT.CLASS_WALLET_SUBMIT_REQUEST,
		};
	},

	getClassWalletCustomerInfo() {
		return {
			url: "/api/classwallet/getCustomerInfo",
			type: AT.CLASS_WALLET_GET_CUSTOMER_INFO_REQUEST,
			method: "GET",
			whenDisconnected: "failFast",
		};
	},

	// Account page endpoints

	loadAccount() {
		return {
			url: "/api/subscription/get",
			type: AT.LOAD_PARENT_ACCOUNT_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	updateOwner(data) {
		return {
			url: "/api/subscription/updateOwner",
			payload: data,
			type: AT.SAVE_PARENT_FORM_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	removeOwner() {
		return {
			url: "/api/subscription/removeOwner",
			payload: {},
			type: AT.DELETE_PARENT_CREDIT_CARD_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	imitateStudent(student, method = null) {
		return {
			url: "/api/session/imitateStudent",
			payload: {studentID: student.id, method},
			type: AT.IMITATE_STUDENT_REQUEST,
		};
	},

	updateStudent(studentID, platformStudentID, data) {
		const payload = _.extend({}, data, {studentID, platformStudentID});
		return {
			url: "/api/user/updateStudent",
			payload: payload,
			type: AT.SAVE_PARENT_STUDENT_INFO_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	changeParentPassword(originalPassword, password) {
		return {
			url: "/api/user/updateSelf",
			payload: {originalPassword, password},
			type: AT.SAVE_PARENT_PASSWORD_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	updateParentName(firstName, lastName) {
		return {
			url: "/api/user/updateSelf",
			payload: {firstName, lastName},
			type: AT.SAVE_PARENT_NAME_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	changeStudentPassword(studentID, password) {
		return {
			url: "/api/user/updateStudent",
			payload: {studentID, password},
			type: AT.PARENT_SAVE_STUDENT_PASSWORD_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	editSubscription(subscriptionID, data) {
		const payload = _.extend({}, data, {subscriptionID});
		return {
			url: "/api/subscription/modify",
			payload: payload,
			type: AT.SAVE_PARENT_SUBSCRIPTION_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	subGetPrices(planType, grades, shipping, couponCode, generation) {
		return {
			url: "/api/subscription/getPrices",
			payload: {type: planType, grades, shipping, couponCode},
			baseActionData: {generation},
			type: AT.SUBSCRIPTION_CHANGE_GET_PRICES_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	subGetSwitchTypeData({
		subscriptionID,
		grades,
		shipping,
		couponCode,
		generation,
		changeType,
	}) {
		return {
			url: "/api/subscription/getSwitchTypeData",
			payload: {subscriptionID, grades, shipping, couponCode, changeType},
			baseActionData: {generation},
			type: AT.SUBSCRIPTION_CHANGE_GET_SWITCH_TYPE_DATA_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	subVerifyAddress(shipping) {
		return {
			url: "/api/subscription/verifyAddress",
			payload: shipping,
			type: AT.SUBSCRIPTION_VERIFY_SHIPPING_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	subChange({
		callType,
		subscriptionID,
		studentID,
		planType,
		displayName,
		grades,
		shipping,
		prices,
		couponCode,
		bookLevelDeferred,
	}) {
		return {
			url: `/api/subscription/${callType}`,
			payload: {
				subscriptionID,
				studentID,
				planType,
				displayName,
				grades,
				shipping,
				prices,
				couponCode,
				bookLevelDeferred,
			},
			type: AT.FINISH_SUBSCRIPTION_CHANGE_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	subCancel(studentID, subscriptionID, refundBooks, refundOnline) {
		return {
			url: "/api/subscription/cancel",
			payload: {subscriptionID, refundBooks, refundOnline},
			type: AT.FINISH_SUBSCRIPTION_CANCEL_REQUEST,
			baseActionData: {studentID},
			whenDisconnected: "failFast",
		};
	},

	subCancelReason(subscriptionID, reason) {
		return {
			url: "/api/subscription/cancelReason",
			payload: {subscriptionID, reason},
			type: AT.SUBMIT_CANCEL_REASON_REQUEST,
			baseActionData: {subscriptionID},
			whenDisconnected: "failFast",
		};
	},

	loadBookDeferrals() {
		return {
			url: "/api/store/bookDeferral/status",
			type: AT.LOAD_BOOK_DEFERRALS_REQUEST,
			method: "GET",
			whenDisconnected: "failFast",
		};
	},

	loadDeferralPlacement(platformUserID, userID) {
		return {
			url: "/api/user/getPlacementTest",
			payload: {platformUserID, userID},
			type: AT.LOAD_DEFERRAL_PLACEMENT_REQUEST,
			baseActionData: {studentID: userID},
			whenDisconnected: "failFast",
		};
	},

	resolveBookDeferral(deferralID, bundleID) {
		return {
			url: "/api/store/bookDeferral/resolve",
			payload: {deferralID, bundleID},
			type: AT.RESOLVE_BOOK_DEFERRAL_REQUEST,
			baseActionData: {deferralID},
			whenDisconnected: "failFast",
		};
	},

	loadUpcomingPayments() {
		return {
			url: "/api/subscription/getUpcomingPayments",
			type: AT.LOAD_PARENT_UPCOMING_PAYMENTS_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	loadPastPayments(limit, offset) {
		return {
			url: "/api/store/getOrders",
			payload: {limit, offset},
			type: AT.LOAD_PARENT_MORE_PAST_PAYMENTS_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	emailReceipt(orderID) {
		return {
			url: "/api/orders/emailReceipt",
			payload: {orderID},
			type: AT.EMAIL_RECEIPT_REQUEST,
		};
	},

	// School endpoints
	// Many are defined in Slots.js and MultiSlots.js.

	loadSchoolAccounts(purchaseID, wereRostersProcessed) {
		const type = wereRostersProcessed
			? AT.POST_ROSTER_PROCESSING_ACCOUNT_RELOAD_REQUEST
			: AT.LOAD_SCHOOL_ACCOUNTS_REQUEST;
		return {
			url: "/api/school/account/getPurchase",
			payload: {purchaseID},
			type,
			whenDisconnected: "failFast",
		};
	},

	loadSchoolAccountsStudents(purchaseID, wereRostersProcessed) {
		const type = wereRostersProcessed
			? AT.POST_ROSTER_PROCESSING_ACCOUNT_STUDENTS_RELOAD_REQUEST
			: AT.LOAD_SCHOOL_ACCOUNTS_STUDENTS_REQUEST;
		return {
			url: "/api/school/account/getStudents",
			payload: {purchaseID},
			type,
			whenDisconnected: "failFast",
		};
	},

	generateSchoolUsername(purchaseID) {
		return {
			url: "/api/school/account/generateUsername",
			payload: {purchaseID},
			type: AT.GENERATE_SCHOOL_USERNAME_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	updateSchoolAccountStaff(userID, platformUserID, purchaseID, vals) {
		return {
			url: "/api/school/account/updateStaff",
			payload: {userID, platformUserID, ...vals, purchaseID},
			type: AT.UPDATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {userID, vals, scope: "staff"},
			whenDisconnected: "failFast",
		};
	},

	updateSchoolAccountStudent(userID, platformUserID, purchaseID, vals) {
		return {
			url: "/api/school/account/updateStudent",
			payload: {userID, platformUserID, ...vals, purchaseID},
			type: AT.UPDATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {userID, vals, scope: "student"},
			whenDisconnected: "failFast",
		};
	},

	updateSchoolAccountClass(classID, vals) {
		return {
			url: "/api/school/account/updateClass",
			payload: {classID, ...vals},
			type: AT.UPDATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {classID, vals, scope: "class"},
			whenDisconnected: "failFast",
		};
	},

	updateSchoolAccountSchool(schoolID, vals) {
		return {
			url: "/api/school/account/updateSchool",
			payload: {schoolID, ...vals},
			type: AT.UPDATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {schoolID, vals, scope: "school"},
			whenDisconnected: "failFast",
		};
	},

	updateSchoolAccountPurchase(purchaseID, vals) {
		return {
			url: "/api/school/account/updatePurchase",
			payload: {purchaseID, ...vals},
			type: AT.UPDATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {purchaseID, vals, scope: "purchase"},
			whenDisconnected: "failFast",
		};
	},

	createSchoolAccountStaff({purchaseID, vals, role, schoolID, classID}) {
		return {
			url: "/api/school/account/createStaff",
			payload: {purchaseID, ...vals, role, schoolID, classID},
			type: AT.CREATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {vals, scope: "staff", schoolID, classID, role},
			whenDisconnected: "failFast",
		};
	},

	createSchoolAccountStudent({vals, classID}) {
		return {
			url: "/api/school/account/createStudent",
			payload: {...vals, classID},
			type: AT.CREATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {vals, classID, scope: "student"},
			whenDisconnected: "failFast",
		};
	},

	createSchoolAccountSchool({purchaseID, vals}) {
		return {
			url: "/api/school/account/createSchool",
			payload: {purchaseID, ...vals},
			type: AT.CREATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {vals, scope: "school"},
			whenDisconnected: "failFast",
		};
	},

	createSchoolAccountClass({schoolID, vals}) {
		return {
			url: "/api/school/account/createClass",
			payload: {...vals, schoolID},
			type: AT.CREATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
			baseActionData: {schoolID, vals, scope: "class"},
			whenDisconnected: "failFast",
		};
	},

	generateRoster({purchaseID, which, year}) {
		return {
			url: "/api/school/generateRoster",
			payload: {purchaseID, which, year},
			type: AT.ROSTERING_GENERATE_CSVS_REQUEST,
			baseActionData: {
				purchaseID,
				which,
				year,
				scope: "school",
			},
			whenDisconnected: "failFast",
		};
	},

	validateRosters({purchaseID, staffRoster, studentRoster, year}) {
		return {
			url: "/api/school/validateRosters",
			payload: {purchaseID, staffRoster, studentRoster, year},
			type: AT.ROSTERING_VALIDATE_CSVS_REQUEST,
			baseActionData: {
				purchaseID,
				staffRoster,
				studentRoster,
				year,
				scope: "school",
			},
			whenDisconnected: "failFast",
		};
	},

	processRosters({purchaseID, staffRoster, studentRoster, year}) {
		return {
			url: "/api/school/processRosters",
			payload: {purchaseID, staffRoster, studentRoster, year},
			type: AT.ROSTERING_PROCESS_CSVS_REQUEST,
			baseActionData: {
				purchaseID,
				staffRoster,
				studentRoster,
				year,
				scope: "school",
			},
			whenDisconnected: "failFast",
		};
	},

	updateSchoolStaffRole({
		purchaseID,
		role,
		userID,
		schoolID,
		classID,
		enabled,
	}) {
		return {
			url: "/api/school/account/setStaffRole",
			payload: {purchaseID, role, userID, schoolID, classID, enabled},
			type: AT.UPDATE_SCHOOL_STAFF_ROLE_REQUEST,
			baseActionData: {userID, schoolID, classID, enabled, role},
			whenDisconnected: "failFast",
		};
	},

	updateSchoolStudentClass({purchaseID, userID, classID, year}) {
		return {
			url: "/api/school/account/setStudentClass",
			payload: {purchaseID, userID, classID, year},
			type: AT.UPDATE_SCHOOL_STUDENT_CLASS_REQUEST,
			baseActionData: {userID, classID, year},
			whenDisconnected: "failFast",
		};
	},

	setActiveSchoolUser({purchaseID, userID, isStaff, active}) {
		const url = isStaff
			? "/api/school/account/setStaffActive"
			: "/api/school/account/setStudentActive";
		return {
			url,
			payload: {purchaseID, userID, active},
			type: AT.SET_ACTIVE_SCHOOL_ACCOUNTS_USER_REQUEST,
			baseActionData: {userID, purchaseID, isStaff, active},
			whenDisconnected: "failFast",
		};
	},

	setActiveSchoolAccountScope({schoolID, classID, active}) {
		if (classID) {
			return {
				url: "/api/school/account/setClassActive",
				payload: {classID, active},
				type: AT.SET_ACTIVE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
				baseActionData: {classID, active},
				whenDisconnected: "failFast",
			};
		} else if (schoolID) {
			return {
				url: "/api/school/account/setSchoolActive",
				payload: {schoolID, active},
				type: AT.SET_ACTIVE_SCHOOL_ACCOUNTS_SCOPE_REQUEST,
				baseActionData: {schoolID, active},
				whenDisconnected: "failFast",
			};
		}
	},

	updateSchoolClassSettings({schoolID, classID, purchaseID, settings}) {
		const settingName = _.keys(settings)[0];
		const settingValue = _.values(settings)[0];
		return {
			url: "/api/school/account/updateClassSettings",
			payload: {schoolID, classID, settingName, settingValue, purchaseID},
			type: AT.UPDATE_SCHOOL_CLASS_SETTINGS_REQUEST,
			baseActionData: {schoolID, classID, settings},
			whenDisconnected: "failFast",
		};
	},

	assignBlock({classID, blockID, changeType}) {
		return {
			url: "/api/class/homework/assignBlock",
			payload: {classID, blockID, changeType},
			type: AT.ASSIGN_BLOCK_FOR_CLASS_REQUEST,
			baseActionData: {classID, blockID, changeType},
			whenDisconnected: "failFast",
		};
	},

	addSeats(payload) {
		return {
			url: "/api/school/account/addSeats",
			payload: payload,
			type: AT.ADD_SEATS_REQUEST,
			baseActionData: payload,
			whenDisconnected: "failFast",
		};
	},

	// Beast Builder

	loadBeast() {
		return {
			url: "/api/beast/fetch",
			payload: {},
			type: AT.LOAD_STUDENT_BEAST_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	loadPartsInventory() {
		return {
			url: "/api/beast/getInventory",
			payload: {},
			type: AT.LOAD_BEAST_INVENTORY_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	saveBeast(beast) {
		return {
			url: "/api/beast/save",
			payload: {beast},
			type: AT.SAVE_BEAST_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	finishBeast(beast) {
		return {
			url: "/api/beast/finish",
			payload: {beast},
			type: AT.FINISH_BEAST_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	setAvatarBeast(avatarData, forSave) {
		return {
			url: "/api/user/updateSelf",
			payload: {avatarData},
			baseActionData: {setToProfile: true, savedToGallery: forSave},
			type: AT.SET_AVATAR_BEAST_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	saveToBeastGallery(avatarData) {
		return {
			url: "/api/beast/updateGallery",
			payload: {avatarData, action: "add"},
			baseActionData: {savedToGallery: true, setToProfile: false},
			type: AT.SET_AVATAR_BEAST_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	removeFromBeastGallery(avatarData, moreInfo) {
		return {
			url: "/api/beast/updateGallery",
			payload: {avatarData, action: "remove", moreInfo},
			type: AT.DELETE_GALLERY_BEAST_REQUEST,
			whenDisconnected: "failFast",
		};
	},

	// Other

	loadCMSInfoPage(filePath) {
		return {
			url: "/api/cms/fetchInfoPage",
			payload: {path: filePath},
			type: AT.LOAD_CMS_INFO_PAGE_REQUEST,
		};
	},

	logMarketingSource(msParams, clientDeviceID) {
		return {
			url: "/api/analytics/logMarketingSource",
			payload: {msParams, clientDeviceID},
			whenDisconnected: "failFast",
			type: AT.LOG_MARKETING_SOURCE_REQUEST,
		};
	},

	logMarketingEvents(events) {
		return {
			url: "/api/analytics/logMarketingEvents",
			payload: {events},
			whenDisconnected: "failFast",
			type: AT.LOG_MARKETING_EVENTS_REQUEST,
		};
	},

	unimitate(newURL) {
		return {
			url: "/api/user/unimitate",
			type: AT.UNIMITATE_REQUEST,
			baseActionData: {newURL},
			payload: {},
		};
	},

	addOneOffReward(payload) {
		return {
			url: "/api/user/addOneOffReward",
			payload,
			type: AT.ADD_ONE_OFF_REWARD_REQUEST,
		};
	},

	setOneOffRewardNotificationShown(payload) {
		return {
			url: "/api/user/setOneOffRewardNotificationShown",
			payload,
			type: AT.SET_ONE_OFF_REWARD_NOTIFICATION_SHOWN_REQUEST,
		};
	},

	// Dev endpoints

	devUnlockBlocksInChapter(chapterID) {
		return {
			url: "/debug/unlockBlocksByChapterID",
			payload: {chapterID},
		};
	},

	triggerDevEndpoint() {
		return {
			url: "/debug/ping",
			type: AT.DEV_ENDPOINT_REQUEST,
			payload: {},
		};
	},

	fetchLeaderboardsRankings(data) {
		// data should contain isAcademyUser, date, metric,
		// usernameFilter, timeWindow, level, campus
		return {
			url: "/api/leaderboards/fetch",
			payload: data,
			type: AT.FETCH_LEADERBOARDS_RANKINGS_REQUEST,
			whenDisconnected: "doNothing",
		};
	},

	debugSetXP(newXPTotal, userID) {
		return {
			url: "/api/user/debugSetXP",
			payload: {xp: newXPTotal, userID},
			type: AT.DEBUG_SET_XP_REQUEST,
		};
	},

	debugResetLevelNotification(userID, level) {
		return {
			url: "/api/user/debugResetLevelNotification",
			payload: {userID, level},
			type: AT.DEBUG_CLEAR_LEVEL_NOTIFICATIONS_REQUEST,
		};
	},

	debugImportTrainerDataForUser(userID) {
		return {
			url: "/api/data/importUser",
			payload: {userID},
			type: AT.DEBUG_IMPORT_TRAINER_DATA_FOR_USER_REQUEST,
		};
	},

	debugImportTrainerDataForClass(classID) {
		return {
			url: "/api/data/importClass",
			payload: {classID},
			type: AT.DEBUG_IMPORT_TRAINER_DATA_FOR_CLASS_REQUEST,
		};
	},

	getLabLockSchedule(classID) {
		return {
			url: "/api/report/getLabLockSchedule",
			payload: {classID},
			type: AT.LOAD_LAB_LOCK_SCHEDULE_REQUEST,
		};
	},

	updateLabLockSchedule(classID, daysToLock, daysToUnlock, disabled) {
		return {
			url: "/api/report/updateLabLockSchedule",
			payload: {classID, daysToLock, daysToUnlock, disabled},
			type: AT.UPDATE_LAB_LOCK_SCHEDULE_REQUEST,
		};
	},

	devAddNewStudent(payload) {
		return {
			url: "/api/subscription/devAddNewStudent",
			payload,
			type: AT.DEV_ADD_NEW_STUDENT_REQUEST,
			whenDisconnected: "failFast",
		};
	},
};

// Reminder: if we ever implement logging of api calls, we need to strip data
// out of some of those calls.

//
// Exported methods
//

const makeAPIForVault = function(vault, clock) {
	const socketsAvailable = !!global.io;
	// Socket wrapper that handles timeouts, pinging, etc.
	const socketQuery = {};
	if (global.BA_SOCKET_TOKEN) {
		socketQuery.socketToken = global.BA_SOCKET_TOKEN;
	}
	const wrappedSocket = new SocketWrapper({
		createSocket: () =>
			global.io &&
			global.io(socketUrl, {
				query: socketQuery,
				transports: ["websocket", "polling"],
			}),
		noPingOnTimeout: true,
		timeout: 20000,
	});
	// See .lock, .unlock below.
	let lockErr = null;
	const vaultAPI = {};

	// This just avoids creating a function object for each doStandardFetchCall.
	const dispatch = action => vault.dispatch(action);

	// For certain actions like logout, pending API requests are no longer
	// applicable. To implement this, we attach the number of ignores we've
	// received to each API call and verify the number hasn't increased by the
	// time of the response.
	let ignoreGeneration = 0;
	vaultAPI.ignorePendingAPIResponses = function() {
		ignoreGeneration += 1;
	};

	// All calls to doStandardFetchCall in here pass this as the last argument.
	const getAPICallMiscData = function() {
		return {
			lockErr,
			getIgnoreGeneration: () => ignoreGeneration,
			now: clock.now(),
		};
	};

	// When school support super users are imitating, we send bypassImitation:
	// true for these endpoints so the backend may allow the request.
	const superUserBypassUrls = new Set([
		"/api/school/account/updateStudent",
		"/api/school/account/updateClass",
		"/api/school/account/updateStaff",
		"/api/school/account/createSchool",
		"/api/school/account/createClass",
		"/api/school/account/createStudent",
		"/api/school/account/createStaff",
		"/api/school/account/setClassActive",
		"/api/school/account/setSchoolActive",
		"/api/school/account/setStaffActive",
		"/api/school/account/setStaffRole",
		"/api/school/account/setStudentActive",
		"/api/school/account/setStudentClass",
		"/api/school/account/updateClassSettings",
		"/api/school/setClassLockOverride",
	]);

	for (const name in mainMethods) {
		if (mainMethods.hasOwnProperty(name)) {
			vaultAPI[name] = (...args) => {
				const params = mainMethods[name](...args);
				const miscData = getAPICallMiscData();
				params.frontendUser = vault.state.user;
				const user = vault.state.user || {};
				const isImitatorWithBypass =
					user.imitatorUserID &&
					(user.imitatorRoles || []).indexOf("ipl-ops-admin") >= 0;
				const superUserMode = !!(
					typeof window !== "undefined" && window.superUserMode
				);
				const bypassImitation =
					superUserBypassUrls.has(params.url) &&
					superUserMode &&
					isImitatorWithBypass;
				if (bypassImitation) {
					params.payload = params.payload || {};
					params.payload.bypassImitation = true;
				}
				doStandardFetchCall(dispatch, params, miscData);
			};
		}
	}

	// Error logging goes through as little machinery as possible. In particular,
	// it skips the vault and the request locking. cb takes (err, errList), the
	// save error and the errors that did not save. Neither is provided if the
	// save is successful.
	vaultAPI.logErrors = function(userID, errorList, cb) {
		const realCb = err => (err ? cb(err, errorList) : cb(null));
		window
			.fetch("/api/user/logFrontendErrors", {
				method: "POST",
				headers: {"Content-Type": "application/json"},
				body: JSON.stringify({userID, errorList}),
			})
			.then(() => {
				realCb(null);
			})
			.catch(ex => {
				realCb(ex);
			});
	};

	if (socketsAvailable) {
		// Set up listeners for the connection.
		const dispatchSetConnected = newState => {
			dispatch({
				type: AT.SET_CONNECTED,
				connected: newState.connected,
				pingingSocket: newState.pingingSocket,
			});
		};
		const throttledDispatchSetConnected = _.throttle(
			dispatchSetConnected,
			2000,
			{leading: false, trailing: true}
		);
		wrappedSocket.onConnectedChange(newState => {
			if (newState.connected) {
				throttledDispatchSetConnected.cancel();
				dispatchSetConnected(newState);
			} else {
				throttledDispatchSetConnected(newState);
			}
		});
	}

	// Locking the API means all actions fail with the provided error.
	vaultAPI.isLocked = function() {
		return !!lockErr;
	};
	vaultAPI.lock = function(err) {
		lockErr = err;
	};
	vaultAPI.unlock = function() {
		lockErr = null;
	};

	if (process.env.NODE_ENV !== "production") {
		let numFake = 1;
		/* eslint-disable no-console */
		global.triggerEndpoint = function(url, payload) {
			const requestNum = numFake;
			numFake += 1;

			const params = {
				url,
				payload,
				type: AT.DEV_ENDPOINT_REQUEST,
				whenDisconnected: "failFast",
				noNeedForReturn: true,
			};
			const miscData = getAPICallMiscData();
			console.log("Fake dev endpoint request", requestNum, url);
			const fakeDispatch = action =>
				console.log("Fake dev endpoint response", requestNum, action);
			doStandardFetchCall(fakeDispatch, params, miscData);
		};
		/* eslint-enable no-console */
	}

	return vaultAPI;
};

module.exports = {
	makeAPIForVault,
};
